import React, { useState, useEffect, useCallback } from "react";
import { Routes, Route, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import BrandCreateForm from "../brand/BrandCreateForm";
import { BrandPopulationProgress } from "../brand/BrandPopulationProgress";
import OrbitalLoader from "../common/OrbitalLoader";
import { brandsApi } from "../../redux/api/brandsApi";
import { setCurrentBrand } from "../../redux/brand/brandSlice";
import { authService } from "../../services/auth";
import { brandsService } from "../../services/brands";

const BrandCreatePanel = ({ projectHash }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleBrandCreated = (brand) => {
    if (projectHash) {
      dispatch(
        brandsApi.util.invalidateTags([{ type: "Brands", id: "LIST" }]),
      );
    }
    if (brand?.hash) {
      dispatch(setCurrentBrand(brand));
      navigate(`populating/${brand.hash}/`);
    }
  };

  return (
    <BrandCreateForm
      showHeader={false}
      projectHash={projectHash}
      createLabel="Add Brand"
      onCreated={handleBrandCreated}
    />
  );
};

const BrandPopulatingPanel = ({ onComplete }) => {
  const { hash } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!hash) {
      navigate("/welcome/brand/", { replace: true });
    }
  }, [hash, navigate]);

  const refreshBrand = useCallback(() => {
    if (!hash) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      brandsService.getBrand(
        hash,
        (response) => {
          if (response?.data) {
            dispatch(setCurrentBrand(response.data));
          } else {
            dispatch(setCurrentBrand({ hash }));
          }
          resolve();
        },
        () => {
          dispatch(setCurrentBrand({ hash }));
          resolve();
        },
      );
    });
  }, [dispatch, hash]);

  const handleContinue = () => {
    const finish = () => {
      dispatch(
        brandsApi.util.invalidateTags([{ type: "Brands", id: "LIST" }]),
      );
      onComplete();
    };

    authService.fetchUser(
      () => {
        refreshBrand().then(finish);
      },
      () => {
        refreshBrand().then(finish);
      },
    );
  };

  if (!hash) {
    return null;
  }

  return (
    <BrandPopulationProgress
      brandHash={hash}
      brandUrl="/brand/overview/"
      onBeforeNavigateToBrand={refreshBrand}
      onContinueClick={handleContinue}
      continueLabel="Continue"
    />
  );
};

const BrandStep = ({ onComplete }) => {
  const { currentProject, loaded: projectsLoaded } = useSelector(
    (state) => state.project,
  );
  const [bootError, setBootError] = useState(null);
  const [bootLoading, setBootLoading] = useState(true);

  const projectHash = currentProject?.hash;

  useEffect(() => {
    if (!projectsLoaded) {
      setBootLoading(true);
      return;
    }

    if (currentProject?.hash) {
      setBootLoading(false);
      setBootError(null);
      return;
    }

    setBootError("Could not prepare your workspace");
    setBootLoading(false);
  }, [currentProject?.hash, projectsLoaded]);

  if (bootLoading || (!projectHash && !bootError)) {
    return (
      <div className="flex items-center justify-center py-8">
        <OrbitalLoader size={96} text="Preparing your workspace..." />
      </div>
    );
  }

  if (bootError) {
    return (
      <div className="rounded-md p-4 border border-red-500 border-opacity-50 bg-red-500 bg-opacity-10">
        <h3 className="text-sm font-medium text-red-500">{bootError}</h3>
      </div>
    );
  }

  return (
    <Routes>
      <Route
        index
        element={<BrandCreatePanel projectHash={projectHash} />}
      />
      <Route
        path="populating/:hash/"
        element={<BrandPopulatingPanel onComplete={onComplete} />}
      />
      <Route
        path="populating/:hash"
        element={<BrandPopulatingPanel onComplete={onComplete} />}
      />
    </Routes>
  );
};

export default BrandStep;
